import { useEffect, useRef, useState } from 'react';
import { Printer } from 'lucide-react';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import { Skeleton } from '../ui/Skeleton';
import { Toast } from '../ui/Toast';
import { apiClient } from '../../lib/api-client';

interface ServiceReportEditorDialogProps {
  open: boolean;
  objectName: string | null;
  month: string;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function ServiceReportEditorDialog({
  open,
  objectName,
  month,
  onOpenChange,
  onSaved,
}: ServiceReportEditorDialogProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!open || !objectName) return;
    let cancelled = false;
    setIsLoading(true);
    setHtmlContent('');
    setToast(null);
    void apiClient.getServiceReportContent(objectName).then((content) => {
      if (!cancelled) setHtmlContent(content);
    }).catch((err: any) => {
      if (!cancelled) setToast({ message: err?.message || 'Failed to load the report HTML', type: 'error' });
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, objectName]);

  async function save() {
    if (!objectName) return;
    setIsSaving(true);
    try {
      await apiClient.updateServiceReportContent(objectName, htmlContent);
      setToast({ message: 'Draft saved.', type: 'success' });
      onSaved();
    } catch (err: any) {
      setToast({ message: err?.message || 'Failed to save the report HTML', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidth="6xl">
        <DialogHeader>
          <DialogTitle>{month ? `${month} draft` : 'Report HTML'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <Skeleton className="h-64 w-full" variant="card" />
          ) : (
            <Tabs defaultValue="edit">
              <TabsList>
                <TabsTrigger value="edit">Edit</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>
              <TabsContent value="edit">
                <textarea
                  value={htmlContent}
                  onChange={(event) => setHtmlContent(event.target.value)}
                  spellCheck={false}
                  className="w-full min-h-[24rem] font-mono text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-3"
                />
              </TabsContent>
              <TabsContent value="preview">
                <div className="flex justify-end mb-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => frameRef.current?.contentWindow?.print()}
                  >
                    <Printer className="w-3.5 h-3.5 mr-1" />
                    Print to PDF
                  </Button>
                </div>
                <iframe
                  ref={frameRef}
                  title="Report preview"
                  sandbox="allow-modals allow-same-origin"
                  srcDoc={htmlContent}
                  className="w-full h-[32rem] bg-white border border-gray-200 dark:border-gray-700 rounded"
                />
              </TabsContent>
            </Tabs>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => void save()} disabled={isLoading || isSaving || !htmlContent.trim()}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
      {toast && (
        <div className="fixed top-4 right-4 z-[80]">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}
    </Dialog>
  );
}
